<template>
  <section class="receipt-printer" :data-stage="stage" :data-feed="feed" aria-label="Recibo de tu compra">
    <div ref="machine" class="printer-machine">
      <div class="printer-machine__top"><img class="machine-logo" :src="logo" alt="Fomoplanes"><span class="machine-led" aria-hidden="true"></span></div>
      <div class="printer-display">
        <div class="printer-display__caption"><span>{{ state.caption }}</span><ReceiptIcon name="ticket" /></div>
        <div v-if="order.total !== null" class="printer-display__amount">{{ money(order.total) }}<small>{{ order.currency }}</small></div>
        <div v-else class="printer-display__event">{{ order.event }}</div>
        <div class="printer-display__footer"><span>{{ ticketCount ? ticketCount + (ticketCount === 1 ? ' entrada' : ' entradas') : 'Tu compra' }}<template v-if="order.paymentLabel"> · {{ order.paymentLabel }}</template></span><span class="signal-bars" aria-hidden="true"><i></i><i></i><i></i></span></div>
      </div>
      <div class="printer-machine__label"><span>BUENOS PLANES. BUENOS RECUERDOS.</span><span>FP—01</span></div><div class="printer-slot" aria-hidden="true"></div>
    </div>
    <div class="printer-output">
      <div :key="runId" class="receipt-feed" :class="{ 'is-feeding': stage === 'printing', 'is-ready': stage === 'ready' }" @animationend="finishPrinting">
        <article class="receipt-paper">
          <div class="receipt-paper__masthead"><div class="receipt-paper__brand"><img :src="logo" alt="Fomoplanes"></div><span v-if="ticketCount" class="receipt-edition">{{ String(ticketCount).padStart(2,'0') }}<small>{{ ticketCount === 1 ? 'ENTRADA' : 'ENTRADAS' }}</small></span></div>
          <p class="receipt-paper__intro">{{ state.caption === 'PAGO APROBADO' ? 'RECIBO DE COMPRA' : 'REGISTRO DE COMPRA' }}</p>
          <div class="receipt-rule"></div><p v-if="order.category" class="receipt-paper__category">{{ order.category }}</p><h2>{{ order.event }}</h2>
          <div class="receipt-paper__event"><p><ReceiptIcon name="calendar" />{{ order.shortDate }}<template v-if="order.time"> · {{ order.time }}</template></p><p v-if="order.venue || order.city"><ReceiptIcon name="pin" />{{ order.venue }}{{ order.city ? ' · ' + order.city : '' }}</p></div>
          <template v-if="order.items.length"><div class="receipt-rule"></div><div v-for="item in order.items" :key="item.key" class="receipt-line receipt-line--item"><div><strong>{{ item.quantity }} × {{ item.name }}</strong><small v-if="item.unitPrice !== null">{{ money(item.unitPrice) }} por entrada</small></div><strong v-if="item.total !== null">{{ money(item.total) }}</strong></div></template>
          <dl v-if="order.breakdown.length" class="receipt-breakdown"><div v-for="line in order.breakdown" :key="line.label"><dt>{{ line.label }}</dt><dd>{{ money(line.value) }}</dd></div></dl>
          <template v-if="order.total !== null"><div class="receipt-rule receipt-rule--perforation"></div><div class="receipt-total"><span>{{ state.totalLabel }}</span><strong>{{ money(order.total) }}<small>{{ order.currency }}</small></strong></div></template>
          <div class="receipt-paid"><ReceiptIcon name="ticket" /><span>{{ state.caption }}</span><span v-if="order.paymentLabel">{{ order.paymentLabel }}</span></div>
          <div class="receipt-rule"></div><div class="receipt-paper__closing"><strong>{{ state.closing }}</strong><p>{{ state.note }}</p><span v-if="order.number">{{ order.number }}</span></div><div class="receipt-edge" aria-hidden="true"></div>
        </article>
      </div><div class="printer-slot-shadow" aria-hidden="true"></div>
    </div>
    <div class="printer-status" role="status" aria-live="polite"><span class="printer-status__dot" :class="{ 'is-pulsing': stage !== 'ready' }"></span>{{ statusLabel }}</div>
  </section>
</template>
<script>
import ReceiptIcon from './ReceiptIcon.vue'
import { receiptMoney } from '../../common/purchaseReceipt'
import logo from '../../../../static/LOGOFOMO26_pro.png'

// Original Vue implementation. Motion reference (no source copied):
// https://www.dqnamo.com/experiments/receipt-printer
export default {
  name: 'PurchaseReceiptPrinter',
  components: { ReceiptIcon },
  props: {
    state: { type: Object, required: true },
    order: { type: Object, required: true },
    feed: { type: String, default: 'stepped' },
    replay: { type: Number, default: 0 },
  },
  emits: ['stage-change'],
  data() { return { logo, stage: 'preparing', runId: 0, startTimer: null, finishTimer: null, motionQuery: null, visibilityObserver: null } },
  computed: {
    ticketCount() { return this.order.ticketCount },
    statusLabel() { return { preparing: 'Preparando tu recibo', printing: 'Imprimiendo tu recibo…', ready: this.state.closing }[this.stage] },
  },
  watch: { replay() { this.print() } },
  mounted() {
    this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    this.motionQuery.addEventListener('change', this.handleMotionChange)
    if (this.motionQuery.matches || !('IntersectionObserver' in window)) { this.print(); return }
    // Begin the first feed only when the machine can actually be seen.
    this.visibilityObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) this.print()
    }, { threshold: 0.35 })
    this.visibilityObserver.observe(this.$refs.machine)
  },
  beforeUnmount() {
    this.clearTimers()
    this.visibilityObserver?.disconnect()
    this.motionQuery?.removeEventListener('change', this.handleMotionChange)
  },
  methods: {
    money(value) { return receiptMoney(value, this.order.currency) },
    clearTimers() { clearTimeout(this.startTimer); clearTimeout(this.finishTimer) },
    setStage(stage) { this.stage = stage; this.$emit('stage-change', stage) },
    handleMotionChange() { if (this.motionQuery.matches) { this.clearTimers(); this.setStage('ready') } },
    print() {
      this.visibilityObserver?.disconnect()
      this.clearTimers()
      this.runId += 1
      if (this.motionQuery?.matches) { this.setStage('ready'); return }
      this.setStage('preparing')
      this.startTimer = setTimeout(() => {
        this.setStage('printing')
        // Fallback if the browser suppresses animationend (e.g. background tabs).
        this.finishTimer = setTimeout(() => this.setStage('ready'), 3400)
      }, 650)
    },
    finishPrinting(event) {
      if (event.target !== event.currentTarget || this.stage !== 'printing') return
      clearTimeout(this.finishTimer)
      this.setStage('ready')
    },
  },
}
</script>
